import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { SkeletonCard, SkeletonText } from "../components/ui/Skeletons";
import { toast } from "sonner";
import { TrendingUp, ArrowLeft, Copy, Code, CheckCircle } from 'lucide-react';
import { Trash } from 'lucide-react';
import PageSEO from '../components/PageSEO';


export default function ProjectSettings({ user, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Installation Audit State
  const [auditUrl, setAuditUrl] = useState('');
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  const cleanBaseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000').replace(/\/api\/?$/, '').replace(/\/$/, '');
  const API = `${cleanBaseUrl}/api`;

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`/projects/${projectId}`);
      setProject(response.data);
      if (!auditUrl && response.data.domain) {
          setAuditUrl(response.data.domain);
      }
    } catch (error) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleRunAudit = async () => {
    setAuditing(true);
    setAuditResult(null);
    try {
      const response = await axios.post(`/projects/${projectId}/verify-installation`, { url: auditUrl });
      setAuditResult(response.data);
      if (response.data.overall_status.startsWith('VERIFIED')) {
        toast.success('System check successful!');
      } else if (response.data.overall_status === 'PENDING_EVENTS') {
        toast.info('Direct scan blocked, waiting for traffic.');
      } else {
        toast.error('System check found warnings.');
      }
    } catch (err) {
      toast.error('Could not complete check. Verify URL format.');
      console.error(err);
    } finally {
      setAuditing(false);
    }
  };

  const trackingScript = project ? `<!-- AI Search Advisers Analytics -->
<script>
  (function() {
    window.AISearchAdvisors = {
      projectId: '${project.id}',
      trackingCode: '${project.tracking_code}',
      apiUrl: '${API}'
    };
    
    // Generate session ID & persistent visitor ID
    let sessionId = sessionStorage.getItem('df_session');
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('df_session', sessionId);
    }
    let visitorId = localStorage.getItem('sv_visitor');
    if (!visitorId) {
      visitorId = 'vis_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      localStorage.setItem('sv_visitor', visitorId);
    }
    
    // Track function
    window.AISearchAdvisors.track = function(eventType, eventData) {
      fetch(window.AISearchAdvisors.apiUrl + '/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({
          project_id: window.AISearchAdvisors.projectId,
          tracking_code: window.AISearchAdvisors.trackingCode,
          session_id: sessionId,
          visitor_id: visitorId,
          event_type: eventType,
          page_url: window.location.href,
          page_title: document.title,
          referrer: document.referrer,
          user_agent: navigator.userAgent,
          consent_given: true,
          ...eventData
        })
      }).catch(function(e) { console.error('[AI Search Advisers]', e); });
    };
    // Auto-track pageview
    window.AISearchAdvisors.track('pageview');
    
    // --- Heatmap Tracking ---
    const MAX_BATCH_SIZE = 50;
    let heatmapEvents = [];
    let lastMoveTime = 0;
    let clickTimes = [];
    let maxScroll = 0;

    const trackHeatmap = function(type, data) {
      heatmapEvents.push({ type, data, timestamp: new Date().toISOString() });
      if (heatmapEvents.length >= MAX_BATCH_SIZE) flushHeatmap();
    };

    const flushHeatmap = function() {
      if (heatmapEvents.length === 0) return;
      const payload = {
        project_id: window.AISearchAdvisors.projectId,
        tracking_code: window.AISearchAdvisors.trackingCode,
        session_id: sessionId,
        page_url: window.location.href,
        events: heatmapEvents
      };
      const jsonStr = JSON.stringify(payload);
      
      // navigator.sendBeacon is completely removed because it silently fails
      // during cross-origin requests when using application/json content types.
      // fetch with keepalive: true is the modern, reliable alternative.
      fetch(window.AISearchAdvisors.apiUrl + '/heatmap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        body: jsonStr
      }).catch(function(e) { console.error('[AI Search Advisers heatmap]', e); });
      
      heatmapEvents = [];
    };

    // 1. Mouse Movement (throttled 200ms for smoother trails)
    document.addEventListener('mousemove', function(e) {
      const now = Date.now();
      if (now - lastMoveTime > 200) {
        const vw = document.documentElement.scrollWidth || document.body.scrollWidth;
        const vh = document.documentElement.scrollHeight || document.body.scrollHeight;
        trackHeatmap('mousemove', {
          x: e.pageX, y: e.pageY,
          px: Math.round((e.pageX / vw) * 10000) / 100,
          py: Math.round((e.pageY / vh) * 10000) / 100,
          vw: vw
        });
        lastMoveTime = now;
      }
    }, { passive: true });

    // 2. Clicks & Rage Clicks
    document.addEventListener('click', function(e) {
      const vw = document.documentElement.scrollWidth || document.body.scrollWidth;
      const vh = document.documentElement.scrollHeight || document.body.scrollHeight;
      trackHeatmap('click', {
        x: e.pageX, y: e.pageY,
        px: Math.round((e.pageX / vw) * 10000) / 100,
        py: Math.round((e.pageY / vh) * 10000) / 100,
        vw: vw,
        target: e.target.tagName,
        targetId: e.target.id || '',
        targetClass: (e.target.className || '').toString().substring(0, 100)
      });
      
      // Rage click detection (3 clicks within 100px radius in 2 seconds)
      const now = Date.now();
      clickTimes.push({ time: now, x: e.pageX, y: e.pageY });
      clickTimes = clickTimes.filter(function(c) { return now - c.time < 2000; });
      if (clickTimes.length >= 3) {
        const xs = clickTimes.map(function(c) { return c.x; });
        const ys = clickTimes.map(function(c) { return c.y; });
        const xSpread = Math.max.apply(null, xs) - Math.min.apply(null, xs);
        const ySpread = Math.max.apply(null, ys) - Math.min.apply(null, ys);
        if (xSpread < 100 && ySpread < 100) {
          trackHeatmap('rageclick', { x: e.pageX, y: e.pageY, px: Math.round((e.pageX / vw) * 10000) / 100, py: Math.round((e.pageY / vh) * 10000) / 100 });
          clickTimes = [];
        }
      }
      
      // Dead click detection
      var isClickable = e.target.closest('a, button, input, select, textarea, [role="button"], [tabindex]');
      if (!isClickable) {
        trackHeatmap('deadclick', { x: e.pageX, y: e.pageY, px: Math.round((e.pageX / vw) * 10000) / 100, py: Math.round((e.pageY / vh) * 10000) / 100 });
      }
    }, { passive: true });

    // 3. Scroll Depth
    document.addEventListener('scroll', function() {
      const docHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      const winHeight = window.innerHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      let scrollPercent = 0;
      if (docHeight > winHeight) {
        scrollPercent = Math.min(100, Math.round(((scrollTop + winHeight) / docHeight) * 100));
      } else {
        scrollPercent = 100;
      }
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
      }
    }, { passive: true });

    // Reliable page-leave data flush using multiple events for cross-browser reliability
    var hasFlushedOnLeave = false;
    var flushOnLeave = function() {
      if (hasFlushedOnLeave) return;
      hasFlushedOnLeave = true;
      trackHeatmap('scroll', { depth: maxScroll });
      flushHeatmap();
      // Reset flag after 2s so if user comes back the next leave still fires
      setTimeout(function() { hasFlushedOnLeave = false; }, 2000);
    };
    window.addEventListener('beforeunload', flushOnLeave);
    window.addEventListener('pagehide', flushOnLeave);  // Best for mobile Safari
    window.addEventListener('unload', flushOnLeave);     // Legacy fallback
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') flushOnLeave();
    });

    // Heartbeat every 8s so live-user counter stays accurate in a 10s window
    setInterval(function() {
      window.AISearchAdvisors.track('heartbeat');
    }, 8000);

    // Periodic heatmap flush every 10s — ensures data reaches backend quickly
    setInterval(function() {
      flushHeatmap();
    }, 10000);
  })();
</script>` : '';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(trackingScript);
    setCopied(true);
    toast.success('Tracking code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmText('');
  };

  const handleDeleteProject = async () => {
    if (!project) return;
    if (deleteConfirmText !== project.name) {
      toast.error('Project name does not match. Type the exact project name to confirm.');
      return;
    }
    try {
      setDeleting(true);
      // Call delete endpoint - adjust path if your API uses /api prefix
      await axios.delete(`/projects/${project.id}`);
      toast.success('Project deleted successfully');
      navigate('/dashboard');
    } catch (err) {
      toast.error('Failed to delete project');
    } finally {
      setDeleting(false);
      closeDeleteModal();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background p-6 lg:p-8">
        <PageSEO title="Project Settings | AI Search Advisers" isPrivate={true} />
        <div className="max-w-4xl mx-auto space-y-8">
          <SkeletonText width="w-64" height="h-10" />
          {[1, 2, 3].map(i => (
            <SkeletonCard key={i} className="p-8">
              <SkeletonText width="w-48" height="h-8" className="mb-6" />
              <div className="space-y-4">
                <SkeletonText width="w-full" height="h-4" />
                <SkeletonText width="w-3/4" height="h-4" />
                <SkeletonText width="w-1/2" height="h-4" />
              </div>
            </SkeletonCard>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background transition-colors">
      <PageSEO title="Project Settings | AI Search Advisers" isPrivate={true} />
      {/* Navigation */}
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 lg:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">Project Settings</h3>
            <p className="text-sm sm:text-lg text-slate-600 dark:text-slate-400">Configure tracking and privacy settings</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => {
              if (projectId) {
                localStorage.setItem('selected_project_id', projectId);
              }
              navigate(`/dashboard?projectId=${projectId}`);
            }} 
            className="w-full sm:w-auto shrink-0 min-h-11 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>

        {/* Project Info */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none border border-transparent dark:border-border min-w-0 p-4 sm:p-6 lg:p-8 mb-8" data-testid="project-info-card">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Project Information</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Project Name</label>
              <div className="mt-1 break-all text-sm text-slate-700 dark:text-slate-300 font-mono bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded">{project?.name}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Domain</label>
              <div className="mt-1 break-all text-sm text-slate-700 dark:text-slate-300 font-mono bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded">{project?.domain}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Project ID</label>
              <div className="mt-1 break-all text-sm text-slate-700 dark:text-slate-300 font-mono bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded">
                {project?.id}
              </div>
            </div>
          </div>
        </Card>

        {/* Delete Project */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none border border-transparent dark:border-border min-w-0 p-4 sm:p-6 lg:p-8 mb-8" data-testid="delete-project-card">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Danger Zone</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">Permanently delete this project and all associated data. This action cannot be undone.</p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={openDeleteModal}
                data-testid="open-delete-modal-button"
              >
                <Trash className="w-4 h-4 mr-2" /> Delete Project
              </Button>
            </div>
          </div>
        </Card>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-card border dark:border-border rounded-xl shadow-xl max-w-xl w-full max-h-[calc(100dvh-2rem)] overflow-y-auto p-4 sm:p-6">
              <h3 className="text-xl font-bold dark:text-white mb-2">Confirm Project Deletion</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">To permanently delete the project, type the project name below exactly: <span className="font-medium dark:text-white">{project?.name}</span></p>

              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full border border-slate-200 dark:border-border dark:bg-slate-800 dark:text-white rounded px-3 py-2 mb-4"
                placeholder="Type project name to confirm"
                data-testid="delete-confirm-input"
              />

              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={closeDeleteModal} className="text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleDeleteProject}
                  disabled={deleteConfirmText !== project?.name || deleting}
                  data-testid="confirm-delete-button"
                >
                  {deleting ? 'Deleting...' : 'Delete Project'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Installation Audit (Moved up to avoid AdBlocker truncation) */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none border border-transparent dark:border-border min-w-0 p-4 sm:p-6 lg:p-8 mb-8" data-testid="status-check-card">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Check Installation Status</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">Confirm your scripts are connected and sending data correctly.</p>
          
          <div className="flex flex-col sm:flex-row gap-3 mb-2">
            <input 
              type="text" 
              value={auditUrl} 
              onChange={(e) => setAuditUrl(e.target.value)} 
              className="w-full min-w-0 min-h-11 flex-1 border border-slate-200 dark:border-border dark:bg-slate-800 dark:text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#021426] focus:outline-none"
              placeholder="https://example.com"
            />
            <Button 
               onClick={handleRunAudit} 
               disabled={auditing || !auditUrl}
               className="bg-[#021426] text-white hover:bg-[#021426]/90 w-full sm:w-36 shrink-0 min-h-11 rounded-lg font-medium"
            >
              {auditing ? 'Checking...' : 'Check Now'}
            </Button>
          </div>

          {auditResult && (() => {
            const hasData = auditResult.event_log?.has_data;
            const isInstalled = auditResult.html_scan?.status === 'INSTALLED';
            const isGreen = isInstalled && hasData && (auditResult.overall_status === 'VERIFIED_ACTIVE' || auditResult.overall_status === 'VERIFIED_EVENTS_FLOWING');
            const isAmber = auditResult.overall_status === 'PENDING_EVENTS';
            
            return (
              <div className={`mt-6 p-5 rounded-xl border ${
                isGreen
                  ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
                  : isAmber
                  ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
                  : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                   {isGreen ? (
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                   ) : (
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                        isAmber ? 'bg-amber-500' : 'bg-red-500'
                      }`}>!</div>
                   )}
                   <span className={`text-lg font-bold ${
                      isGreen
                        ? 'text-green-800 dark:text-green-400' 
                        : isAmber
                        ? 'text-amber-800 dark:text-amber-400'
                        : 'text-red-800 dark:text-red-400'
                   }`}>
                      Result: {
                        isGreen 
                          ? 'VERIFIED ACTIVE'
                          : auditResult.overall_status === 'NOT_INSTALLED' || auditResult.html_scan?.status === 'NOT_FOUND'
                          ? 'NOT INSTALLED'
                          : !hasData && auditResult.overall_status === 'INSTALLED_BUT_INACTIVE'
                          ? 'INSTALLED BUT INACTIVE (NO DATA)'
                          : auditResult.overall_status.replace(/_/g, ' ')
                      }
                   </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white dark:bg-slate-800/50 p-4 rounded-lg border border-slate-100 dark:border-slate-700/50">
                     <div className="font-semibold text-slate-700 dark:text-slate-300 mb-2">Source Scan</div>
                     <div className="text-slate-600 dark:text-slate-400 mb-1">
                       Status: <span className={`font-semibold ml-1 ${isInstalled ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{auditResult.html_scan.status.replace(/_/g, ' ')}</span>
                     </div>
                     {auditResult.html_scan.script_location && (
                        <div className="text-slate-600 dark:text-slate-400">Location: <span className="font-mono bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded text-xs ml-1">&lt;{auditResult.html_scan.script_location}&gt;</span></div>
                     )}
                  </div>
                  <div className="bg-white dark:bg-slate-800/50 p-4 rounded-lg border border-slate-100 dark:border-slate-700/50">
                     <div className="font-semibold text-slate-700 dark:text-slate-300 mb-2">Ingestion Flow</div>
                     <div className="text-slate-600 dark:text-slate-400 mb-1">
                       Active: <span className={`font-semibold ml-1 ${hasData ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{hasData ? 'Yes' : 'No'}</span>
                     </div>
                     {auditResult.event_log.last_event_received_at && (
                        <div className="text-slate-600 dark:text-slate-400">Last Ping: <span className="font-semibold text-slate-800 dark:text-slate-200 ml-1">{new Date(auditResult.event_log.last_event_received_at).toLocaleString()}</span></div>
                     )}
                  </div>
                </div>
              </div>
            );
          })()}
        </Card>

        {/* Tracking Code */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none border border-transparent dark:border-border min-w-0 p-4 sm:p-6 lg:p-8 mb-8" data-testid="tracking-code-card">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Ingestion Code</h2>
              <p className="text-slate-600 dark:text-slate-400">Add this code to your website's &lt;head&gt; section</p>
            </div>
            <Button
              data-testid="copy-tracking-code-button"
              onClick={copyToClipboard}
              className="bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] hover:text-[#021426]"
            >
              {copied ? (
                <><CheckCircle className="w-4 h-4 mr-2" />Copied!</>
              ) : (
                <><Copy className="w-4 h-4 mr-2" />Copy Code</>
              )}
            </Button>
          </div>
          <div className="bg-slate-900 rounded-xl p-4 sm:p-6 overflow-x-auto max-w-full">
            <pre className="text-sm text-green-400 font-mono">{trackingScript}</pre>
          </div>
        </Card>

        {/* Privacy Settings */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none border border-transparent dark:border-border min-w-0 p-4 sm:p-6 lg:p-8" data-testid="privacy-settings-card">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Privacy Settings</h2>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-3 border-b border-slate-200 dark:border-slate-800">
              <div>
                <div className="font-semibold text-slate-900 dark:text-white">IP Anonymization</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Hash IP addresses for user privacy</div>
              </div>
              <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                Enabled
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-3 border-b border-slate-200 dark:border-slate-800">
              <div>
                <div className="font-semibold text-slate-900 dark:text-white">Consent Management</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Require user consent before tracking</div>
              </div>
              <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                Enabled
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-3">
              <div>
                <div className="font-semibold text-slate-900 dark:text-white">Respect Do Not Track</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Honor browser DNT settings</div>
              </div>
              <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                Enabled
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
